import { isExternalUrl, normalizeUrl } from '../Pages/DevlopmentOrder/DesignDetail/utils/sanitizeHtml';

/**
 * Block-level tags whose closing (or self-closing) form is a visual line break.
 * Replaced with a space before parsing so `<p>a</p><p>b</p>` reads as `a b`
 * rather than `ab`.
 */
const BLOCK_BOUNDARY = /<\/(p|div|li|ul|ol|h[1-6]|blockquote|tr)>|<br\s*\/?>/gi;

/**
 * Collapses a rich-text (HTML) value down to its visible text.
 *
 * Rich-text fields are stored in the same `String` columns that previously held
 * plain text, so every consumer sees a mix of legacy plain values and HTML.
 * A value with no markup characters is returned untouched, so legacy rows are
 * never re-encoded; anything else is parsed with `DOMParser`, which builds an
 * inert document and never executes scripts or loads resources.
 *
 * @param {string | null | undefined} value - Stored value, plain text or HTML.
 * @returns {string} The visible text, whitespace-collapsed and trimmed.
 */
export const htmlToText = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  if (!/[<&]/.test(value)) {
    return value;
  }
  const spaced = value.replace(BLOCK_BOUNDARY, ' ');
  const parsed = new DOMParser().parseFromString(spaced, 'text/html');
  return (parsed.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

/**
 * Length of a rich-text value measured in visible characters.
 *
 * Character caps are a promise about how much a user may write, so they must be
 * counted on the text: an empty `<strong></strong>` pair costs 17 raw characters
 * and zero visible ones.
 *
 * @param {string | null | undefined} value - Stored value, plain text or HTML.
 * @returns {number} Visible character count.
 */
export const richTextLength = (value?: string | null): number => htmlToText(value).length;

/**
 * Whether a rich-text value carries any visible content. CKEditor serialises an
 * emptied editor as `<p>&nbsp;</p>`, which is not an empty string but is empty
 * to the reader.
 *
 * @param {string | null | undefined} value - Stored value, plain text or HTML.
 * @returns {boolean} True when nothing visible remains.
 */
export const isRichTextEmpty = (value?: string | null): boolean => htmlToText(value).length === 0;

/**
 * Tags the master rich-text toolbar can produce, plus the headings and `<em>` /
 * `<b>` variants legacy values may already carry. Anything else is unwrapped:
 * the element goes, its text stays.
 */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'OL', 'UL', 'LI', 'BLOCKQUOTE', 'SPAN', 'A',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);

/** Elements removed with their content rather than unwrapped. */
const REMOVED_TAG_SELECTOR = 'script, style, iframe, object, embed, form, input, link, meta';

/** Attributes each allowed tag may keep. Every other attribute is dropped. */
const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  A: ['href', 'target', 'rel'],
  SPAN: ['style'],
};

/**
 * The only inline style permitted, so `fontColor` survives without `style`
 * becoming a general-purpose injection surface. A single `color` declaration
 * with a hex, functional, or named value — nothing else, `url()` included.
 */
const SAFE_COLOR_STYLE = /^\s*color\s*:\s*(?:#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([\d\s.,%/-]+\)|[a-z]+)\s*;?\s*$/i;

const BLOCKED_HREF_PROTOCOL = /^\s*(?:javascript|data|vbscript|mailto)\s*:/i;

/**
 * Rewrites an anchor in place: drops an unusable or script-bearing `href`, and
 * marks an off-host link so it opens detached from this tab.
 *
 * @param {Element} element - The anchor being sanitized.
 * @returns {void}
 */
const sanitizeAnchor = (element: Element): void => {
  const href = (element.getAttribute('href') ?? '').trim();
  if (!href || BLOCKED_HREF_PROTOCOL.test(href)) {
    element.removeAttribute('href');
    return;
  }
  const normalized = normalizeUrl(href);
  if (!normalized) {
    element.removeAttribute('href');
    return;
  }
  element.setAttribute('href', normalized);
  if (isExternalUrl(normalized)) {
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer');
  }
};

/**
 * Removes every attribute the tag is not allowed to keep.
 *
 * @param {Element} element - The element being sanitized.
 * @returns {void}
 */
const stripDisallowedAttributes = (element: Element): void => {
  const allowed = ALLOWED_ATTRIBUTES[element.tagName] ?? [];
  /* Snapshot the names: `attributes` is live, and removing while iterating it skips entries. */
  Array.from(element.attributes, (attribute) => attribute.name).forEach((name) => {
    if (!allowed.includes(name)) {
      element.removeAttribute(name);
    }
  });
};

/**
 * Replaces an element with its own children, so a disallowed tag disappears
 * while the text it wrapped survives.
 *
 * @param {Element} element - The element to unwrap.
 * @returns {void}
 */
const unwrap = (element: Element): void => {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

/**
 * Sanitizes a rich-text value against what the master toolbar can actually
 * produce.
 *
 * Deliberately separate from the design-detail `sanitizeHtml`, whose allowlist
 * predates this field: that one keeps neither `<span>`, `<s>` nor `<blockquote>`,
 * so running the master field's output through it silently discards font colour,
 * strikethrough and quotes. It is left untouched because three v1 screens depend
 * on its exact behaviour.
 *
 * Parsing is done with `DOMParser`, which builds an inert document — no script
 * runs and no resource is fetched while the value is being cleaned.
 *
 * @param {string | null | undefined} value - Raw editor output.
 * @returns {string} The value with everything outside the allowlist removed.
 */
export const sanitizeRichTextHtml = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const { body } = parsed;

  body.querySelectorAll(REMOVED_TAG_SELECTOR).forEach((element) => element.remove());

  body.querySelectorAll('*').forEach((element) => {
    stripDisallowedAttributes(element);

    if (element.tagName === 'A') {
      sanitizeAnchor(element);
    }

    if (element.hasAttribute('style') && !SAFE_COLOR_STYLE.test(element.getAttribute('style') ?? '')) {
      element.removeAttribute('style');
    }

    if (!ALLOWED_TAGS.has(element.tagName)) {
      unwrap(element);
    }
  });

  return body.innerHTML;
};
